import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Code2, Eye, Play, Rocket, PanelLeft, Terminal, Monitor, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIDEStore } from '@/stores/useIDEStore';

interface IDEHeaderProps {
  projectName: string;
  projectId: string;
  onRun?: () => void;
  onDeploy?: () => void;
}

export default function IDEHeader({ projectName, projectId, onRun, onDeploy }: IDEHeaderProps) {
  const navigate = useNavigate();
  const { mode, setMode, panelVisibility, togglePanel } = useIDEStore();

  return (
    <div className="h-12 border-b flex items-center justify-between px-3 bg-background">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-medium text-sm truncate max-w-[200px]">{projectName}</span>

        {/* Mode Toggle */}
        <div className="flex items-center border rounded-md overflow-hidden">
          <button
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${
              mode === 'code' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            onClick={() => setMode('code')}
          >
            <Code2 className="h-3 w-3" /> Code
          </button>
          <button
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${
              mode === 'visual' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
            onClick={() => setMode('visual')}
          >
            <Eye className="h-3 w-3" /> Visual
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Panel Toggles */}
        <Button
          variant={panelVisibility.fileExplorer ? 'secondary' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => togglePanel('fileExplorer')}
          title="Toggle File Explorer"
        >
          <PanelLeft className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={panelVisibility.terminal ? 'secondary' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => togglePanel('terminal')}
          title="Toggle Terminal"
        >
          <Terminal className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={panelVisibility.preview ? 'secondary' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => togglePanel('preview')}
          title="Toggle Preview"
        >
          <Monitor className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant={panelVisibility.aiChat ? 'secondary' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => togglePanel('aiChat')}
          title="Toggle AI Chat"
        >
          <MessageSquare className="h-3.5 w-3.5" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Button variant="outline" size="sm" onClick={onRun}>
          <Play className="h-3.5 w-3.5 mr-1" /> Run
        </Button>
        <Button size="sm" onClick={onDeploy}>
          <Rocket className="h-3.5 w-3.5 mr-1" /> Deploy
        </Button>
      </div>
    </div>
  );
}
